import React, {useState, useEffect, useCallback} from 'react'
import {Spinner} from '@material-tailwind/react'
import { debounce } from 'lodash';
import toast, { Toaster } from 'react-hot-toast';
import { v4 as uuidv4 } from 'uuid';
import { useSelector, useDispatch } from 'react-redux';
import { ArrowRightIcon, ArrowLeftIcon } from "@heroicons/react/24/outline";
import { CheckIcon } from '@heroicons/react/24/outline';
import { ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/outline';
import { ChevronDownIcon } from '@heroicons/react/24/outline';

import {
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Typography,
  Button,
  Chip,
  Avatar,
  IconButton,
  Tooltip,
  Input,
  Select, 
  Option,
  Slider,
  Dialog,
  DialogBody,
  Menu,
  MenuHandler,
  MenuList,
  MenuItem
} from "@material-tailwind/react";

import { BitcoinNetworkType, signMessage, signTransaction } from 'sats-connect';
import { formatAddress } from '../../util/format-data';
import { useTranslation } from 'react-i18next';
import { useWallet, useWallets } from '@wallet-standard/react';
import { getAllDomains } from '../../util/api';
import FeeRateCard from '../../components/FeeRateCard';
import { getAllDomainsOnlySrc101, getAllDomainsOnlySrc101Numbers, getAllDomainsOnlySrc101Check, generateSrc101Image, getSrc101Create } from '../../util/stamp-api';
import { updateSrc101Domain } from '../../util/new-api';
import HotDomains from '../../components/Modals/HotDomains';
import BitnameProImage from '../../assets/imgs/bitnamepro.jpg'

export default function DomainName() {
  const wallet = useSelector(state => state.wallet);

  const { wallets } = useWallets();

  const SatsConnectNamespace = 'sats-connect:';

  const isSatsConnectCompatibleWallet = (wallet) => {
      return SatsConnectNamespace in wallet.features;
  }

  const { t, i18n } = useTranslation();
  const feeRates = useSelector(state => state.feeRate);

  const deployTopDomains = ["sats", "btc"]
  const deployInformation = {
    sats : {
      deployHash : "a6aa05d8fb3dd8663197129e65ba67ec32961cd3f27162b79fa467303baaf2a8",
      fee : [20000, 3200000, 1600000, 800000, 400000],
      feeAddress : 'bc1q820agrzwhypsq4lszxqtce8jk9tfh9xtwjmnkv'
    },
    x : {
      deployHash : "e65c733101bf3fcd60fda037caf86c97078cb2ab4036d186b3d853c03b3dce0b",
      fee : [20000, 1600000, 800000, 400000, 200000],
      feeAddress : 'bc1q820agrzwhypsq4lszxqtce8jk9tfh9xtwjmnkv'
    },
    btc : {
      deployHash : "77fb147b72a551cf1e2f0b37dccf9982a1c25623a7fe8b4d5efaac566cf63fed",
      fee : [45000, -1, -1, 900000, 225000],
      feeAddress : 'bc1pj3nkenxdnx4wmda28f7swkgrmmgtwm4567a5uspey92kn7c3kzuq6uavdg'
    },
  }
  const [showHotDomain, setShowHotDomain] = useState(false);
  const [topDomain, setTopDomain] = useState('sats');
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showLoading, setShowLoading] = useState(false);
  const [show, setShow] = useState(false);
  const [domain, setDomain] = useState('');
  const [address, setAddress] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(10);
  const [key, setKey] = useState('');
  const [feeRateMode, setFeeRateMode] = useState("Normal");
  const [feerate, setFeerate] = useState(0);
  const feeRateTabs = ["Slow", "Normal", "Fast", "Custom"];
  const [isValid, setIsValid] = useState(false);
  const [domainNameError, setDomainNameError] = useState('');

  const [feeValues, setFeeValues] = useState({
    "inscriptionFee": 0,
    "networkFee": 0,
    "serviceFee": 0,
    "royaltyFee": 0,
    "totalFee": 0
  });
  const [customFee, setCustomFee] = useState(0);
  const [sliderValue, setSliderValue] = useState(2);

  useEffect(() => {
    const updateFees = async () => {
      if (feeRateMode == "Custom") {
        setFeerate(customFee);
      }
      else
      {
        setFeerate(feeRates[feeRateMode]);
        if (customFee == 0) 
        {
          setCustomFee(feeRates["Fast"] + 1);
        }
      }
    }

    updateFees();

  }, [feeRateMode, customFee, feeRates])

  useEffect(() => {
    let value = (sliderValue / 100) * (500 - feeRates["Normal"]) + feeRates["Normal"];
    setCustomFee(Math.floor(value));
  }, [sliderValue])

  useEffect(() => {
    updateDomains(key, page);
  }, [page, topDomain])

  useEffect(() => { 
    setAddress(wallet.nostrOrdinalsAddress);
  }, [wallet.nostrOrdinalsAddress]);

  const updateDomains = async (value, offset) => {
    setLoading(true);
    let data = await getAllDomainsOnlySrc101(value, topDomain, offset, limit);
    setDomains(data.data);
    setTotal(Math.ceil(data.total / limit))
    setLoading(false);
  }

  const next = () => {
    if (page === total) return;
    setPage(page + 1);
  };
 
  const prev = () => {
    if (page === 1) return;
    setPage(page - 1);
  };

  const debouncedSearch = useCallback(debounce((value) => {
    setPage(1);
    updateDomains(value, 1)
  }, 300), []); 

  const handleSearch = (value) => {
    setKey(value);
    debouncedSearch(value);
  };

  const handleDomainSelect = (value) => {
    setTopDomain(value);
  };

  const handleCustomFee = (value) => {
    const sanitizedValue = value.replace(/[^0-9.]/g, '');
    setCustomFee(sanitizedValue);
  }

  const handleDomain = (value) => {
    let tempValue = value.toLowerCase();
    setDomain(tempValue);
    debounceCheckDomainValid(tempValue);
  }
 
  const debounceCheckDomainValid = useCallback(debounce(async (value) => {
    if (isValidString(value)) {
      setDomainNameError('')
    } else setDomainNameError(t('Insert Valid Domain Name Error Message'));

    if (value == "") {
      setIsValid(false);
      return;
    }

    if (topDomain == "btc" &&  (value.length == 1 || value.length == 2)) {
      setIsValid(false);
      return;
    }

    let res = await getAllDomainsOnlySrc101Check(value, topDomain);
    if (res.status) {
      setIsValid(false);
    }
    else {
      setIsValid(true);
    }
  }, 500), []);

  const isValidString = (input) => {
    // Define the regex pattern for invalid characters and unicode spaces
    const invalidPattern = /[@#￥……&*（）——|{}【】《》'；：“”‘。，、？\s\u0009\u000A\u000B\u000C\u000D\u0020\u0085\u00A0\u1680\u2000\u2001\u2002\u2003\u2004\u2005\u2006\u2007\u2008\u2009\u200A\u2028\u2029\u202F\u205F\u3000\u180E\u200B\u200C\u200D\u2060\uFEFF]/;
  
    // Test the input string against the invalid pattern
    return !invalidPattern.test(input);
  }
  

  const handleRegister = async () => {
    setShowLoading(true);
    setShow(true);
    if (wallet.nostrOrdinalsAddress == "")
    {
      toast.error(t('Connect Wallet Error Message'));
      setShowLoading(false);
      setShow(false);
      return;
    }

    if (domain == "")
    {
      toast.error(t('Insert Domain Error Message'));
      setShowLoading(false);
      setShow(false);
      return;
    }

    if (address == "")
    {
      toast.error(t('Insert Address Error Message'));
      setShowLoading(false);
      setShow(false);
      return;
    }

    // check domain availablity
    let res = await getAllDomainsOnlySrc101Check(domain, topDomain);
    if (res.status) {
      toast.error(t('Domain Already Minted Error Message'));
      setShowLoading(false);
      setShow(false);
      return;
    }

    if (domainNameError != "") {
      toast.error(t('Insert Valid Domain Name Error Message'));
      setShowLoading(false);
      setShow(false);
      return;
    }

    let param = {
      sourceAddress: wallet.nostrPaymentAddress,
      changeAddress: wallet.nostrPaymentAddress,
      op: "mint",
      hash: deployInformation[topDomain].deployHash,
      toaddress: address,
      recAddress: deployInformation[topDomain].feeAddress,
      tokenid: [ btoa(domain) ],
      dua: "999",
      prim: "true",
      coef: "1000",
      sig: "",
      img: topDomain == 'btc' ? [ `https://img.bitname.pro/img/${domain}.png`] : [ `https://tapwallet.io/img/${topDomain}/${domain}.${topDomain}.png`],
      feeRate: Math.ceil(feerate * 10)
    }

    let psbtResult = await getSrc101Create(param);
    if (psbtResult.status != undefined && psbtResult.status == "error" ) {
      toast.error(psbtResult.error);
      setShowLoading(false);
      setShow(false);
      return;
    }

    let isSuccess = true;
    
    try{
      if (wallet.domain == "tapwallet") {
        const signedPsbt = await window.tapwallet.signPsbt(psbtResult.hex);
        const txid = await window.tapwallet.pushPsbt(signedPsbt);
      }
      else if (wallet.domain == "unisat") {
        const signedPsbt = await window.unisat.signPsbt(psbtResult.hex);
        const txid = await window.unisat.pushPsbt(signedPsbt);
      }
      else if (wallet.domain == "okxwallet") {
        const signedPsbt = await window.okxwallet.bitcoin.signPsbt(psbtResult.hex);
        const txid = await window.okxwallet.bitcoin.pushPsbt(signedPsbt);
      }
      else if (wallet.domain == "xverseWallet") {
        let signIndexes = [];
        for(let i=0;i<psbtResult.inputsToSign.index; i++){
          signIndexes.push(i);
        }

        await signTransaction({
          payload: {
              network: {
                  type: BitcoinNetworkType.Mainnet,
              },
              psbtBase64: psbtResult.hex,
              broadcast: true,
              message: "tip the author! Don't worry this will not be broadcasted.",
              inputsToSign: [
                  {
                      address: wallet.nostrPaymentAddress,
                      signingIndexes: signIndexes,
                  },
              ],
          },
          onFinish: async (response) => {
          },
          onCancel: () => {
            alert(t('Request canceled'));
            setShowLoading(false);
            isSuccess = false;
            setShow(false);
          },
        });
      }
      else if (wallet.domain == "magiceden") {
        let signIndexes = [];
        for(let i=0;i<psbtResult.inputsToSign.index; i++){
          signIndexes.push(i);
        }

        let magicedenWallets = wallets.filter(isSatsConnectCompatibleWallet);

        await signTransaction({
          getProvider: async () =>
            magicedenWallets[0].features['sats-connect:'].provider,
          payload: {
              network: {
                  type: BitcoinNetworkType.Mainnet,
              },
              psbtBase64: psbtResult.hex,
              broadcast: true,
              message: "tip the author! Don't worry this will not be broadcasted.",
              inputsToSign: [
                  {
                      address: wallet.nostrPaymentAddress,
                      signingIndexes: signIndexes,
                  },
              ],
          },
          onFinish: async (response) => {
          },
          onCancel: () => {
            alert(t('Request canceled'));
            setShowLoading(false);
            isSuccess = false;
            setShow(false);
          },
        });
      }
      else {
        toast.error("This wallet does not support yet. Please wait!");
        setLoading(false);
        setShow(false);
        return;
      }
    }
    catch(e)
    {
      console.log(e);
      alert(t('User Reject Request Error Message'));
      isSuccess = false;
      setShowLoading(false);
      setShow(false);
      return;
    }
    
    if (isSuccess) {
      if (topDomain != "btc") {
        await generateSrc101Image(topDomain, domain);
      }
      await updateSrc101Domain(domain + "." + topDomain, address, btoa(domain), domain, topDomain);

      toast.success(t('Name Registration Successful Message'));
      updateDomains('', 1);
      setPage(1);
    }
    setShowLoading(false);
  }

  const handleCopy = (value, mode) => {
    navigator.clipboard.writeText(value).then(
      () => {
        // Successfully copied to clipboard
        if (mode == 1)
          toast.success(t('Name Copied!'));
        else
          toast.success(t('Address Copied!'));
      },
      (err) => {
        // Failed to copy to clipboard
        console.error('Could not copy address: ', err);
      }
    );
  }

  return (
    <div className="text-white mt-[65px] w-full max-w-[1500px] md:px-7 px-2 flex flex-col justify-center items-center mb-10 min-h-[600px]">
      {loading ? <Spinner className="h-16 w-16 text-gray-900/50 fixed" color="pink"/> : <></>}
      <img src={BitnameProImage} className="w-[150px] mt-10 rounded-full"/>
      <div className="text-white font-bold sm:text-[36px] text-[24px] mt-5 text-center font-title">{t('SRC-101 DOMAIN NAME REGISTRATION')}</div>
      <div className="md:min-w-[600px] min-w-[300px] border-gray-800 border-[1px] border-solid p-8 rounded-lg mt-5 flex flex-col gap-8 bg-[#081817] md:w-[600px] w-full">
        <div className="w-full">
          {/* <Select 
            label={t('Top Level Domain')} 
            className="text-white"
            menuProps={{
              className: "bg-black",
            }}
            placeholderclassname="text-white"
            onChange={handleDomainSelect}
            value={topDomain}
          >
            <Option value = "sats" className="text-white bg-black">.SATS</Option>
            <Option value = "x" className="text-white bg-black">.X</Option>
          </Select> */}
           {/* <Input type="text" color="white" label = {t('Top Level Domain')} className="w-full md:min-w-[500px] min-w-[250px]" value={".sats"} readOnly/> */}
        </div>
        <div>
          <div className="ml-1 text-primary cursor-pointer" onClick={() => {setShowHotDomain(true)}}>
            {t('Hot Available Domains')} &gt;&gt;
          </div>
          <div className="flex flex-row w-full items-center">
            <div className="relative md:w-[75%] w-[65%]">
              <Input type="text" color="white" label = {t('Domain Name')} className="w-full" value={domain} onChange = {(e) => {handleDomain(e.target.value)}}/>
              <Menu>
                <MenuHandler>
                  <div className="text-primary absolute md:top-0 top-[1px] font-bold right-2 text-[20px] border-solid boder-white border-l-[2px] py-1 pl-1 cursor-pointer flex flex-row gap-2 items-center">
                    <span>.{topDomain}</span>
                    <ChevronDownIcon strokeWidth={4} className="h-4 w-4" /></div>
                </MenuHandler>
                <MenuList className="bg-black border-black text-white w-[200px]">
                  {
                    deployTopDomains.map((data) => 
                      <MenuItem onClick={() => handleDomainSelect(data)} key={uuidv4()}>{data} </MenuItem>
                    )
                  }
                </MenuList>
              </Menu>
              {
                isValid ?
                <div className="text-primary absolute right-[90px] top-2 font-bold">
                  <CheckIcon strokeWidth={2} className="h-5 w-5" /> 
                </div>
                :
                <></>
              }
            </div>
            <div className="md:w-[25%] w-[35%] flex flex-row gap-[2px] text-primary font-bold text-[18px] items-center justify-end">
              <svg width="18" height="18" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="9" cy="9" r="9" fill="#e91d9b"></circle><path d="M13.14 7.965c.185-1.143-.624-1.672-1.078-1.964-.452-.292-1.143-.502-1.143-.502l.442-1.78-1.02-.256-.442 1.78-.815-.206.442-1.78L8.494 3l-.442 1.78-2.157-.542-.262 1.056s1.065.266 1.182.297c.12.029.104.09.094.13-.01.04-1.34 5.405-1.36 5.476-.016.072-.028.113-.127.09l-1.131-.284L4 12.176l2.088.523-.423 1.702 1.113.279.423-1.703.693.174-.423 1.703 1.06.266.424-1.702.798.2c.53.133 2.544.158 2.982-1.611.439-1.77-1.012-2.519-1.012-2.519s1.23-.38 1.417-1.523Zm-2.417 3.282c-.21.847-1.155.912-1.37.857l-1.895-.474.56-2.262 1.965.492c.377.096.951.54.74 1.387Zm.35-3.445c-.224.902-1.124.861-1.408.791L8.28 8.246l.534-2.153 1.264.316c.216.054 1.22.492.995 1.393Z" fill="#fff"></path></svg>
              <div className="mt-[2px] flex flex-row">
                <span>
                  {
                    domain.length == 0 ? deployInformation[topDomain].fee[0] / 100000000 : domain.length > 4 ? deployInformation[topDomain].fee[0] / 100000000 : deployInformation[topDomain].fee[domain.length] == -1 ? 0 :
                    deployInformation[topDomain].fee[domain.length] /100000000
                  }
                </span>
                <span className="md:flex hidden">&nbsp;BTC</span>
              </div>
            </div>
          </div>
          {
            domainNameError != "" ?
            <div className="text-red-800 mt-1 font-bold flex flex-row gap-[2px] items-center">
              <ExclamationCircleIcon strokeWidth={2} className="h-5 w-5" /> 
              <span>{domainNameError}</span>
            </div> 
            :
            <></>
          }
        </div>
        <div>
          <Input type="text" color="white" label = {t('Recipient')} className="w-full md:min-w-[500px] min-w-[250px]" 
          value = {address}
          onChange = {(e) => {setAddress(e.target.value)}}/>
        </div>
        <div className="sm:text-[18px] text-[14px]">
          {t('Select Network Fee You Want')}:
        </div>
        <div className="grid grid-cols-3 gap-3">
          <FeeRateCard header={feeRateTabs[1]} rate={feeRates[feeRateTabs[1]]} active={feeRateMode} onClick={() => {setFeeRateMode(feeRateTabs[1])}}/>
          <FeeRateCard header={feeRateTabs[2]} rate={feeRates[feeRateTabs[2]]} active={feeRateMode} onClick={() => {setFeeRateMode(feeRateTabs[2])}}/>
          <FeeRateCard header={feeRateTabs[3]} rate={customFee} active={feeRateMode} onClick={() => {setFeeRateMode(feeRateTabs[3])}}/>
        </div>
        {
          feeRateMode == "Custom" ? 
          <div className="flex flex-row gap-3 items-center">
            <Slider className="sm:flex hidden" color="blue" value = {sliderValue} onChange = {(e) => setSliderValue(e.target.value)}/>
            <div className="sm:max-w-[200px] w-full">
              <Input type="text" color="white" label = {t('Custom Fee')} className="sm:max-w-[200px] w-full" value={customFee} onChange = {(e) => {handleCustomFee(e.target.value)}}/>
            </div>
          </div>
          : 
          <></>
        }
        <div className="text-[20px]">
          {t('Expiration Time')} : <span className="text-primary font-bold">{t('Permanent')}</span>
        </div>
        <div className="flex flex-row w-full justify-center">
          <button className="bg-primary hover:bg-primary-hover rounded-md py-2 min-w-[240px]" onClick={handleRegister}>{t('Register')}</button>
        </div>
      </div>
      <HotDomains type = {topDomain} showHotDomain = {showHotDomain} setShowHotDomain = {setShowHotDomain} handleDomain = {handleDomain}/>
      <Dialog
        open={show}
        className="bg-gray-900 border-gray-600 border-solid border-[1px]"
        size={"xs"}
      >
        <DialogBody>
          <div className='flex flex-col gap-3 w-full items-center'>
            {
              showLoading ? 
                <div className="flex flex-col justify-center items-center gap-4 py-10">
                  <div className="flex flex-row text-primary font-bold text-[32px]">{t('Please Wait')}</div>
                  <Spinner className="h-16 w-16 text-gray-900/50" color="pink"/>
                </div>
                : 
                <div className="flex flex-col justify-center items-center gap-4 py-10 md:px-6 px-2">
                  <CheckCircleIcon strokeWidth={2} className="h-24 w-24 text-primary" /> 
                  <div className="flex flex-row text-primary font-bold sm:text-[24px] text-[20px] text-center">{t('Domain Successfully Registered')}</div>
                  <button className="bg-gray-600 rounded-md py-2 sm:min-w-[200px] min-w-[120px] my-3 text-white" onClick={() => setShow(false)}>{t('CLOSE')}</button>
                </div>
            }
          </div>
        </DialogBody>
      </Dialog>
      <div className="md:min-w-[600px] md:w-[600px] min-w-[300px] flex flex-col mt-10 mb-10 w-full">
        <div className="text-white font-bold text-lg w-full text-center border-b-[1px] border-b-[#24242a] pb-1 mb-3">{t('Latest registrations')}</div>
        <Input type="text" color="white" label = {t('Search by name or address')} className="w-full md:min-w-[500px] min-w-[250px]" value = {key}
                    onChange = {(e) => {handleSearch(e.target.value)}}/>
        <div className="w-full border-gray-800 border-[1px] border-solid rounded-lg mt-4 bg-[#081817]">
          <div className="w-full grid grid-cols-2 py-4 px-6 bg-[#1e1f1f]">
            <div>{t('Name')}</div>
            <div>{t('Address')}</div>
          </div>
          {
            domains.length == 0 ? <div className="w-full flex flex-row py-4 px-6 text-gray-500 hover:bg-[#121313] justify-center">{t('No Names')}</div> : 
            <>
              {
                domains.map((data) => {
                  return (
                    <div className="w-full grid grid-cols-2 py-4 px-6 text-gray-500 hover:bg-[#121313] cursor-pointer" key={uuidv4()}>
                      <div onClick={() => handleCopy(data.domain, 1)}>{data.domain}</div>
                      <div onClick={() => handleCopy(data.address, 2)}>{formatAddress(data.address)}</div>
                    </div>
                  )
                })
              }
            </>
          }
        </div>
        {
          total > 1 ? 
          <div className="flex flex-row items-center gap-8 justify-end mt-5">
            <IconButton
              size="sm"
              variant="outlined"
              onClick={prev}
              disabled={page === 1}
              className="bg-primary hover:bg-primary-hover"
            >
              <ArrowLeftIcon strokeWidth={2} className="h-4 w-4" />
            </IconButton>
            <Typography color="white" className="font-normal">
              {t('Page')} <strong className="text-primary">{page}</strong> of{" "}
              <strong className="text-primary">{total}</strong>
            </Typography>
            <IconButton
              size="sm"
              variant="outlined"
              onClick={next}
              disabled={page === total}
              className="bg-primary hover:bg-primary-hover"
            >
              <ArrowRightIcon strokeWidth={2} className="h-4 w-4" />
            </IconButton>
          </div>
          :
          <></>
        }
      </div>
    </div>
  )
}
